import { call, put, takeEvery, takeLatest, cancelled } from 'redux-saga/effects';
import {
  CASE_GENERATE_ANSWERS_WITH_CITATIONS_DATA_BEGIN,
  CASE_GENERATE_ANSWERS_WITH_CITATIONS_DATA_SUCCESS,
  CASE_GENERATE_ANSWERS_WITH_CITATIONS_DATA_FAILURE,
  CASE_GENERATE_ANSWERS_WITH_CITATIONS_DATA_DISMISS_FEEDBACK,
  CASE_CANCEL_GENERATE_ANSWERS_WITH_CITATIONS_DATA,
} from './constants';
import api from 'common/api';
import { withCurrentCaseId } from 'common/selectors';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

export const generateAnswersWithCitationsData = createPromiseAction(
  CASE_GENERATE_ANSWERS_WITH_CITATIONS_DATA_BEGIN,
);

export const cancelGenerateAnswersWithCitationsData = () => ({
  type: CASE_CANCEL_GENERATE_ANSWERS_WITH_CITATIONS_DATA,
});

// Store for tracking cancellation state and AbortController
let isCancellationRequested = false;
let currentAbortController = null;

export function dismissGenerateAnswersWithCitationsDataFeedback() {
  return {
    type: CASE_GENERATE_ANSWERS_WITH_CITATIONS_DATA_DISMISS_FEEDBACK,
  };
}

export function* doGenerateAnswersWithCitationsData(action) {
  const {
    payload: {
      caseId,
      searchQuery = '',
      historyId = '',
      filters,
      outputType,
      version,
      promptCategory,
      promptName,
      compare,
    },
  } = action;

  // Cancel any existing request
  if (currentAbortController) {
    currentAbortController.abort();
  }

  // Reset cancellation flag and create new AbortController
  isCancellationRequested = false;
  currentAbortController = new AbortController();

  try {
    // Build request payload to match expected API shape.
    const postPayload = { searchQuery: searchQuery };
    if (filters) {
      postPayload.filters = filters; // expected shape: { folders: [...], files: [...] }
    }
    if (outputType) {
      postPayload.outputType = outputType; // e.g., 'table', 'list', etc.
    }
    // Direct answers flow should receive version, not numMatches
    postPayload.version = version && version !== '' ? version : 'latest';
    if (historyId) {
      postPayload.searchHistoryId = historyId;
    }
    // Add prompt metadata if provided
    if (promptCategory) {
      postPayload.promptCategory = promptCategory;
    }
    if (promptName) {
      postPayload.promptName = promptName;
    }
    // Add compare field if provided
    if (compare) {
      postPayload.compare = compare;
    }
    const res = yield call(api.post, `/cases/${caseId}/answers-direct`, postPayload, {
      signal: currentAbortController.signal,
    });

    // Check if cancellation was requested while the call was in progress
    if (isCancellationRequested) {
      // Request was cancelled after it completed - resolve as cancelled
      currentAbortController = null;
      yield call(resolvePromiseAction, action, { cancelled: true });
      return;
    }

    // Clear the controller on successful completion
    currentAbortController = null;

    if (res && res.error) {
      yield put({
        type: CASE_GENERATE_ANSWERS_WITH_CITATIONS_DATA_FAILURE,
        feedback: {
          message: 'feedback.generateAnswersFailure',
          error: res.error,
        },
      });
      return yield call(rejectPromiseAction, action, res.error);
    }

    yield put({
      type: CASE_GENERATE_ANSWERS_WITH_CITATIONS_DATA_SUCCESS,
      data: res,
    });
    yield call(resolvePromiseAction, action, res);
  } catch (error) {
    // Clear the controller on error
    currentAbortController = null;

    // Check if it was an abort error (cancellation)
    if (
      error.name === 'AbortError' ||
      (error.message &&
        (error.message.includes('aborted') || error.message.includes('signal is aborted'))) ||
      isCancellationRequested
    ) {
      // Request was cancelled - resolve as cancelled to prevent promise rejection
      yield call(resolvePromiseAction, action, { cancelled: true });
      return;
    }

    // Handle other errors
    yield put({
      type: CASE_GENERATE_ANSWERS_WITH_CITATIONS_DATA_FAILURE,
      feedback: {
        message: 'feedback.generateAnswersFailure',
        error: error.message,
      },
    });
    yield call(rejectPromiseAction, action, error);
  } finally {
    // Handle saga cancellation
    if (yield cancelled()) {
      if (currentAbortController) {
        currentAbortController.abort();
        currentAbortController = null;
      }
      isCancellationRequested = true;
    }
  }
}

// worker Saga: will be fired on CASE_CANCEL_GENERATE_ANSWERS_WITH_CITATIONS_DATA actions
// eslint-disable-next-line require-yield
export function* doCancelGenerateAnswersWithCitationsData() {
  // Set the cancellation flag
  isCancellationRequested = true;

  // Abort the current HTTP request
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
  }
}

export function* watchGenerateAnswersWithCitationsData() {
  yield takeLatest(
    generateAnswersWithCitationsData,
    withCurrentCaseId(doGenerateAnswersWithCitationsData),
  );
}

export function* watchCancelGenerateAnswersWithCitationsData() {
  yield takeEvery(
    CASE_CANCEL_GENERATE_ANSWERS_WITH_CITATIONS_DATA,
    doCancelGenerateAnswersWithCitationsData,
  );
}

export function useGenerateAnswersWithCitationsData() {
  const dispatch = useDispatch();

  const boundCancelAction = useCallback(() => {
    return dispatch(cancelGenerateAnswersWithCitationsData());
  }, [dispatch]);

  return {
    cancelGenerateAnswersWithCitationsData: boundCancelAction,
  };
}
